/**
 * Funding care WITHOUT selling the home — the two ways, and the engine offered neither.
 *
 * WHAT WAS MISSING. Every aged care candidate that needed more money than the client had liquid
 * either sold the home, rented it, or simply paid a smaller deposit. Borrowing against the home was
 * never on the table, although Manual Control has offered it all along and the projection engine has
 * always modelled both schemes.
 *
 * That is a large hole in a module whose hardest question is usually "do we have to sell Mum's
 * house?". Families who want to keep the property — because a protected person lives there, because
 * the market is poor, because it is the family home — were shown no way to do it beyond paying the
 * daily fee out of a shrinking bank account.
 *
 * THE TWO SCHEMES, and they are not interchangeable:
 *
 *   HOME EQUITY ACCESS SCHEME (HEAS) — the government's own scheme, at a materially lower rate than
 *   any commercial product. Paid as a fortnightly stream, capped at 150% of the maximum pension
 *   rate, and the payments are NOT income for the pension. For a resident paying a daily fee out of
 *   cashflow it is almost always the cheaper of the two, and it is the one advisers reach for first.
 *   It cannot, however, produce a lump sum for a deposit.
 *
 *   REVERSE MORTGAGE — commercial, dearer, and the only one of the two that can raise a LUMP SUM.
 *   That is what makes it the option when the goal is to pay a refundable deposit rather than a
 *   daily fee: the deposit removes the daily accommodation payment, is refunded to the estate, and
 *   is exempt from the pension assets test, so borrowing to pay it can leave the family ahead even
 *   after the interest. Whether it actually does is precisely what a ranking is for.
 *
 * BOTH COMPOUND, AND NEITHER IS FREE. Nothing is repaid along the way, so the debt grows every year
 * against a house that may or may not grow faster, and it comes out of the estate at the end. The
 * candidates exist so that trade-off is priced rather than guessed at — not because borrowing is
 * usually right.
 */
import { PENSION_RATES } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { RadFundingScenario } from './suggestionEngineFunding.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

/**
 * The published HEAS rate. Lower than any commercial reverse mortgage, which is most of why the
 * scheme is worth modelling separately rather than as "a cheaper reverse mortgage".
 */
export const HEAS_INTEREST_RATE = 3.95;

/** A representative commercial reverse-mortgage rate. Manual Control's own default. */
export const REVERSE_MORTGAGE_INTEREST_RATE = 8.0;

/**
 * How much of the home a lender will advance, by age. Reverse mortgage lending is age-banded because
 * the loan is repaid from the estate: the older the borrower, the shorter the expected term and the
 * more a lender will release. Roughly the industry rule of thumb — 15% at 60, rising a point a year.
 */
function maxLoanToValue(age: number): number {
    if (!(age > 0)) return 0.2;
    return Math.min(0.45, Math.max(0.15, 0.15 + (age - 60) * 0.01));
}

/** Fractions of the maximum advance tried, so the ranking can find the least borrowing that works. */
const LOAN_FRACTIONS = [0.5, 1];

/**
 * HEAS payment levels tried, as a share of the scheme's cap.
 *
 * The cap is 150% of the maximum pension rate combined (pension plus HEAS), so the most the scheme
 * can add to a full pensioner is 50% of the maximum rate. Modelling the cap and half of it is enough
 * to show the shape without a projection per dollar.
 */
const HEAS_FRACTIONS = [0.5, 1];

const FORTNIGHTS_PER_YEAR = 26;

export interface EquityReleaseContext {
    /** The room price, so a lump-sum candidate can be pointed at the deposit. */
    radAmount: number;
    /** Basic daily fee + hotelling, annualised — what the DAP is added to. */
    otherAnnualFees: number;
    /** MPIR, for the daily accommodation payment on whatever is left unpaid. */
    MPIR: number;
    /** Living expenses the scenario should carry. */
    scenarioLivingExpenses?: number | string;
}

const ageOf = (dob: string | undefined): number => {
    if (!dob) return 0;
    const born = new Date(dob);
    if (Number.isNaN(born.getTime())) return 0;
    const now = new Date();
    let age = now.getFullYear() - born.getFullYear();
    const m = now.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < born.getDate())) age -= 1;
    return age;
};

/**
 * Add the borrow-against-the-home candidates.
 *
 * Nothing is added when there is no home to borrow against, and nothing is added for a home the
 * client is also selling in the same scenario — the two are alternatives, not a combination, and
 * pairing them would produce candidates that borrow against a house that has been sold.
 */
export function appendEquityReleaseScenarios(
    scenarios: RadFundingScenario[],
    factFind: DraftFactFind,
    context: EquityReleaseContext,
): void {
    const homeValue = Number(factFind?.home?.value) || 0;
    if (homeValue <= 0) return;

    const { radAmount, otherAnnualFees, MPIR, scenarioLivingExpenses } = context;
    const isCouple = factFind?.adviceFor === 'couple';
    const clientAge = ageOf(factFind?.client?.dob);
    const partnerAge = ageOf(factFind?.partner?.dob);
    // A lender prices on the YOUNGER borrower — the loan runs until the last of them leaves.
    const lendingAge = isCouple && partnerAge > 0 ? Math.min(clientAge, partnerAge) : clientAge;

    const living = scenarioLivingExpenses ?? factFind.expenses?.livingExpenses;

    /*
     * HEAS — a fortnightly stream against the daily fees, with the home kept.
     *
     * The cap is on the COMBINED pension plus HEAS at 150% of the maximum rate, so what the scheme
     * can add depends on what pension is already being paid. Using 50% of the maximum rate as the
     * ceiling is the full-pensioner case and understates nothing for anyone else: a part pensioner
     * can draw more, so a candidate built on this figure is conservative in the safe direction.
     */
    const maxRateAnnual = isCouple ? Number(PENSION_RATES.couple ?? 0) : Number(PENSION_RATES.single ?? 0);
    const heasCeilingAnnual = maxRateAnnual * 0.5;

    if (heasCeilingAnnual > 0) {
        for (const fraction of HEAS_FRACTIONS) {
            const annual = Math.round(heasCeilingAnnual * fraction);
            const perFortnight = Math.round(annual / FORTNIGHTS_PER_YEAR);
            if (perFortnight <= 0) continue;
            scenarios.push({
                /* Named in full for the same reason: "HEAS" is an acronym, not a description. */
                name: `Home Equity Access Scheme loan: $${perFortnight.toLocaleString()}/fortnight against the home, pay the DAP, keep the home`,
                radFunding: {},
                groupKey: `heas-${Math.round(fraction * 100)}`,
                fundingOrderLabel: 'Home Equity Access Scheme',
                strategyOptions: {
                    annualDap: radAmount * MPIR + otherAnnualFees,
                    rentHome: false,
                    downsizeHome: false,
                    scenarioLivingExpenses: living,
                    accessHomeEquity: true,
                    heas: true,
                    heasDetails: {
                        paymentAmount: perFortnight,
                        paymentFrequency: 'fortnightly',
                        interestRate: HEAS_INTEREST_RATE,
                    },
                },
                equityReleaseRationale:
                    `The government's Home Equity Access Scheme pays a fortnightly amount against the home at `
                    + `${HEAS_INTEREST_RATE}% — far below a commercial reverse mortgage — and the payments are not counted as `
                    + `income for the Age Pension. The home is kept. Nothing is repaid along the way, so the debt grows each `
                    + `year and comes out of the estate when the house is sold.`,
            });
        }
    }

    /*
     * REVERSE MORTGAGE — a lump sum, pointed at the deposit.
     *
     * `useForRad` is what makes this worth modelling rather than just being expensive debt: whatever
     * the deposit absorbs is exempt from the pension assets test AND removes that much daily
     * accommodation payment AND is refunded to the estate. The interest still compounds against it,
     * which is exactly the comparison the ranking exists to make.
     */
    const maxLoan = Math.floor(homeValue * maxLoanToValue(lendingAge));
    if (maxLoan >= 50_000 && radAmount > 0) {
        for (const fraction of LOAN_FRACTIONS) {
            const loanAmount = Math.min(radAmount, Math.round(maxLoan * fraction));
            if (loanAmount < 50_000) continue;
            const radCovered = Math.min(loanAmount, radAmount);
            const pctOfRoom = Math.round((radCovered / radAmount) * 100);
            scenarios.push({
                /*
                 * NAME THE PRODUCT (owner, 2026-09-01: "where a reverse mortgage is used this should
                 * be called out in the title").
                 *
                 * It read "Borrow $800k against the home for 100% of the room, keep the home", which
                 * describes an ordinary loan. A reverse mortgage is not an ordinary loan - there are
                 * no repayments, the interest compounds untouched, and it is settled out of the
                 * house. A reader who does not know which product they are looking at cannot know
                 * which questions to ask about it.
                 */
                name: `Reverse mortgage: borrow ${`$${Math.round(loanAmount / 1000)}k`} against the home for ${pctOfRoom}% of the room, keep the home`,
                radFunding: {},
                groupKey: `reverse-mortgage-${pctOfRoom}`,
                fundingOrderLabel: 'Reverse mortgage',
                strategyOptions: {
                    annualDap: Math.max(0, radAmount - radCovered) * MPIR + otherAnnualFees,
                    rentHome: false,
                    downsizeHome: false,
                    scenarioLivingExpenses: living,
                    accessHomeEquity: true,
                    reverseMortgage: true,
                    radInput: radAmount,
                    reverseMortgageDetails: {
                        loanAmount,
                        loanPercentage: Math.round((loanAmount / homeValue) * 100),
                        interestRate: REVERSE_MORTGAGE_INTEREST_RATE,
                        loanStructure: 'lump_sum',
                        regularPaymentAmount: 0,
                        regularPaymentFrequency: 'fortnightly',
                        establishmentFees: 0,
                        useForRad: true,
                    },
                },
                equityReleaseRationale:
                    `Borrowing against the home pays a refundable deposit without selling it. The deposit removes that much `
                    + `daily accommodation payment, is exempt from the Age Pension assets test, and is refunded when care ends. `
                    + `Against that, the loan compounds at ${REVERSE_MORTGAGE_INTEREST_RATE}% with nothing repaid along the way, `
                    + `and is settled from the house — so whether this leaves the family ahead depends on the room price, the `
                    + `rates and how long the stay is.`,
            });
        }
    }
}
